/**
 * /dashboard — Customer dashboard.
 *
 * Layout (responsive):
 *   Mobile / Tablet  : single-column; tax calendar accordion at the bottom
 *   Desktop (≥ 1280) : two-column [main | sticky sidebar]
 *
 * Sections:
 *   1. Welcome header
 *   2. Profile-completion nudge (when isProfileComplete === false)
 *   3. Financial Overview cards  ← derived from service requests
 *   4. Service Dashboard         ← Available / Ongoing / Completed tabs
 *   5. Tax Calendar              ← sidebar (XL) or accordion (mobile)
 */
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  AlertCircle, BarChart3, ArrowRight, BookOpen, Calculator, Sparkles,
  Scale, FileSearch, HeartPulse, Wallet, Lock, Coins,
} from 'lucide-react';
import { ErrorMessage, Skeleton } from '@finmin/ui';
import { formatCurrency } from '@finmin/utils';
import { useProfile } from '@/api/profile';
import { useMyServiceRequests } from '@/api/service-requests';
import { usePanView } from '@/features/dashboard/pan-view-context';
import { useRewardWallet } from '@/api/rewards';
import { formatPoints, pointsToInr } from '@/features/rewards/utils';
import { OverviewCards }    from '@/features/dashboard/components/overview-cards';
import { ServiceDashboard } from '@/features/dashboard/components/service-dashboard';
import { TaxCalendar }      from '@/features/dashboard/components/tax-calendar';

export const Route = createFileRoute('/_protected/dashboard')({
  component: DashboardPage,
});

// ── Palette ───────────────────────────────────────────────────────────────────

const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';
const CREAM  = 'hsl(var(--palette-cream,   40 100% 98%))';

// ── Page ──────────────────────────────────────────────────────────────────────

function DashboardPage() {
  const { data: profile }                = useProfile();
  const { selection } = usePanView();
  const { data: requests = [], isLoading: requestsLoading, error: requestsError } =
    useMyServiceRequests(selection === 'all' ? undefined : selection);

  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM }}>

      {/* ── Page hero / greeting ──────────────────────────────────────────
          Padding (`px-4 sm:px-6`) lives on the inner max-w-7xl container so
          the heading aligns with the navbar logo and the dashboard cards
          below — all three share the same effective left edge.
          Vertical padding kept compact (py-5/6) so the hero + financial
          overview + service grid all fit a 720p+ viewport without scroll. */}
      <div
        className="py-5 sm:py-6 relative overflow-hidden"
        style={{ backgroundColor: FOREST }}
      >
        {/* Subtle decorative gradient — adds depth without competing for attention. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full blur-3xl opacity-20 animate-float-slow"
          style={{ backgroundColor: AMBER }}
        />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 relative">
          <h1
            className="font-display font-semibold text-2xl sm:text-3xl xl:text-4xl leading-tight tracking-tight mb-1 max-w-3xl animate-slide-up-fade"
            style={{ color: CREAM }}
          >
            Welcome to your Financial Operating System.
          </h1>
          <p
            className="text-xs sm:text-sm animate-slide-up-fade stagger-1 max-w-2xl"
            style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.75)' }}
          >
            Here's everything happening with your financial life.
          </p>
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 sm:py-5">

        {/* Profile nudge */}
        {profile && !profile.isProfileComplete && (
          <div
            className="rounded-2xl border p-4 flex items-start gap-3 mb-6 animate-slide-down-fade"
            style={{
              backgroundColor: 'hsl(var(--warning) / 0.06)',
              borderColor:     'hsl(var(--warning))',
            }}
          >
            <AlertCircle
              className="h-5 w-5 shrink-0 mt-0.5"
              style={{ color: 'hsl(var(--warning))' }}
              aria-hidden
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">Complete your profile</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add your PAN, date of birth, and financial details to unlock all services.
              </p>
            </div>
            <Link
              to="/questionnaire"
              className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 transition-all hover:scale-[1.03] active:scale-95 group/cta"
              style={{ backgroundColor: `${FOREST}15`, color: FOREST }}
            >
              Complete
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/cta:translate-x-0.5" aria-hidden />
            </Link>
          </div>
        )}

        {/* API error */}
        <ErrorMessage message={requestsError?.message ?? null} />

        {/* Two-column layout: main | sidebar */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 xl:gap-6 xl:items-start">

          {/* ── Left column — tighter vertical rhythm so 2 sections fit. ── */}
          <div className="min-w-0 space-y-5">

            {/* Financial Overview */}
            <section className="space-y-3">
              <SectionHeading icon={<BarChart3 className="h-5 w-5" style={{ color: AMBER }} aria-hidden />}>
                Financial Overview
              </SectionHeading>
              <OverviewCards
                requests={requests}
                isLoading={requestsLoading}
                totalMoneySaved={profile?.total_money_saved}
              />
            </section>

            {/* Service Dashboard (tabs) */}
            <ServiceDashboard requests={requests} isLoading={requestsLoading} />
          </div>

          {/* ── Right column — CTAs + Tax Calendar ────────────────────────── */}
          <div className="space-y-4">
            {/* Rewards wallet — balance at a glance, links to /rewards */}
            <RewardsBalanceCta />

            {/* Plan tax regime (cold-start tool) — top-of-column CTA */}
            <PlanTaxCta />

            {/* Learn Income Tax Act, 2025 — promotional card linking to /it-act */}
            <LearnItActCta />

            {/* More tools — coming-soon roadmap */}
            <MoreToolsPanel />

            {/* Visible only on XL+ */}
            <div className="hidden xl:block">
              <TaxCalendar variant="sidebar" />
            </div>

            {/* Accordion on mobile/tablet */}
            <div className="xl:hidden">
              <TaxCalendar variant="accordion" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section heading helper ────────────────────────────────────────────────────

function SectionHeading({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0">{icon}</span>
      <h2 className="font-display font-semibold text-lg text-foreground tracking-tight">
        {children}
      </h2>
    </div>
  );
}

// ── Rewards balance CTA — wallet at a glance, links to /rewards ─────────────
//
// Forest tile mirroring LearnItActCta's tone: shows the live points balance
// (≈ ₹ value) from /rewards/wallet and routes to the full wallet + redeem flow.

function RewardsBalanceCta() {
  const { data, isLoading } = useRewardWallet();
  const balance = data?.available_balance ?? 0;

  return (
    <Link
      to="/rewards"
      aria-label="Rewards & Wallet — view balance and redeem points"
      className="group block rounded-2xl overflow-hidden relative transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 animate-slide-up-fade"
      style={{ backgroundColor: FOREST }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-10 -right-10 h-36 w-36 rounded-full blur-2xl opacity-25 animate-float-slow"
        style={{ backgroundColor: AMBER }}
      />
      <div className="relative p-4 sm:p-5 flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
          style={{ backgroundColor: 'hsl(var(--palette-cream, 40 100% 98%) / 0.14)' }}
        >
          <Coins className="h-5 w-5" style={{ color: AMBER }} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] font-semibold uppercase tracking-widest mb-0.5"
            style={{ color: AMBER }}
          >
            Reward points
          </p>
          {isLoading ? (
            <Skeleton className="h-6 w-24 rounded" />
          ) : (
            <p
              className="font-display font-semibold text-xl leading-tight tracking-tight tabular-nums"
              style={{ color: CREAM }}
            >
              {formatPoints(balance)} pts
              <span
                className="ml-2 text-xs font-normal"
                style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.7)' }}
              >
                ≈ {formatCurrency(pointsToInr(balance))}
              </span>
            </p>
          )}
        </div>
        <ArrowRight
          className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
          style={{ color: AMBER }}
          aria-hidden
        />
      </div>
    </Link>
  );
}

// ── Plan tax CTA — opens the cold-start Tax Regime Selector tool ────────────
//
// Cream-on-bg card mirroring `LearnItActCta`'s shape but inverted in tone
// (light background, forest text) so the right column reads as two
// complementary tiles — "learn the law" + "use it to plan your tax".

function PlanTaxCta() {
  return (
    <Link
      to="/tax-regime-selector"
      aria-label="Plan next year's tax — Tax Regime Selector"
      className="group block rounded-2xl overflow-hidden relative border border-border bg-background transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:border-foreground/15 animate-slide-up-fade"
    >
      {/* Subtle amber blob in the corner — same float-slow keyframe as LearnItActCta */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-12 -right-12 h-36 w-36 rounded-full blur-2xl opacity-30 animate-float-slow"
        style={{ backgroundColor: AMBER }}
      />
      <div className="relative p-4 sm:p-5 flex items-start gap-3">
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
          style={{ backgroundColor: 'hsl(var(--palette-mint, 154 38% 94%))' }}
        >
          <Calculator className="h-5 w-5" style={{ color: FOREST }} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] font-semibold uppercase tracking-widest mb-0.5"
            style={{ color: AMBER }}
          >
            <span className="inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" aria-hidden />
              FY 2026-27
            </span>
          </p>
          <h3
            className="font-display font-semibold text-base sm:text-lg leading-tight tracking-tight"
            style={{ color: FOREST }}
          >
            Plan next year's tax
          </h3>
          <p className="text-xs mt-1 leading-relaxed text-muted-foreground">
            Old vs New regime in 5 steps. See the verdict, what-if sliders and ranked savings.
          </p>
          <span
            className="inline-flex items-center gap-1 mt-2.5 text-xs font-semibold transition-transform"
            style={{ color: AMBER }}
          >
            Start planning
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── Learn Income Tax Act, 2025 — promo CTA above the Tax Calendar ───────────

function LearnItActCta() {
  return (
    <Link
      to="/it-act"
      aria-label="Learn Income Tax Act, 2025"
      className="group block rounded-2xl overflow-hidden relative transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 animate-slide-up-fade"
      style={{ backgroundColor: FOREST }}
    >
      {/* Decorative amber blob — float-slow keyframe already defined globally */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full blur-2xl opacity-25 animate-float-slow"
        style={{ backgroundColor: AMBER }}
      />
      <div className="relative p-4 sm:p-5 flex items-start gap-3">
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
          style={{ backgroundColor: 'hsl(var(--palette-cream, 40 100% 98%) / 0.14)' }}
        >
          <BookOpen className="h-5 w-5" style={{ color: AMBER }} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] font-semibold uppercase tracking-widest mb-0.5"
            style={{ color: 'hsl(var(--palette-amber, 33 100% 50%))' }}
          >
            <span className="inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" aria-hidden />
              New
            </span>
          </p>
          <h3
            className="font-display font-semibold text-base sm:text-lg leading-tight tracking-tight"
            style={{ color: 'hsl(var(--palette-cream, 40 100% 98%))' }}
          >
            Learn Income Tax Act, 2025
          </h3>
          <p
            className="text-xs mt-1 leading-relaxed"
            style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.72)' }}
          >
            Search any 1961 section's new home. CBDT-verified mappings.
          </p>
          <span
            className="inline-flex items-center gap-1 mt-2.5 text-xs font-semibold transition-transform"
            style={{ color: 'hsl(var(--palette-amber, 33 100% 50%))' }}
          >
            Explore the mapper
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── More tools — coming-soon roadmap ────────────────────────────────────────
//
// Four upcoming tools rendered as compact cards in a 2×2 grid.  Each card
// uses the same visual language as PlanTaxCta / LearnItActCta — rounded-2xl
// border, mint icon pill, Forest title, animated entrance — but at half the
// vertical footprint and with a disabled treatment (lock badge, "Coming
// soon" pill, cursor-not-allowed) so they read as roadmap rather than live
// destinations.  The 2-column grid keeps the Tax Calendar from being pushed
// way below the fold.
//
// Roadmap items (config-driven — flip one to live by replacing
// `<ComingSoonCta>` with a real `<Link>` and adding the route).

interface UpcomingTool {
  id:    string;
  Icon:  React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  /** Tiny one-line tagline shown under the title.  Keep ≤ 32 chars. */
  tagline: string;
}

const UPCOMING_TOOLS: UpcomingTool[] = [
  // {
  //   id:      'old-vs-new-regime',
  //   Icon:    Scale,
  //   title:   'Old vs New Regime',
  //   tagline: 'Year-end forecast',
  // },
  {
    id:      'notice-analyzer',
    Icon:    FileSearch,
    title:   'Notice Analyzer',
    tagline: 'Decode IT notices',
  },
  {
    id:      'past-itr-health-check',
    Icon:    HeartPulse,
    title:   'Past ITR Health Check',
    tagline: 'Audit last 3 returns',
  },
  {
    id:      'salary-optimiser',
    Icon:    Wallet,
    title:   'Salary Optimiser',
    tagline: 'Restructure your CTC',
  },
];

function MoreToolsPanel() {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-1">
        More tools
      </p>
      <div className="grid grid-cols-2 gap-3">
        {UPCOMING_TOOLS.map((tool, idx) => (
          <ComingSoonCta key={tool.id} tool={tool} index={idx} />
        ))}
      </div>
    </div>
  );
}

function ComingSoonCta({ tool, index }: { tool: UpcomingTool; index: number }) {
  const Icon    = tool.Icon;
  const stagger = `stagger-${Math.min((index % 8) + 1, 8)}`;

  return (
    <div
      role="button"
      aria-disabled="true"
      title="Coming soon"
      className={`relative rounded-2xl border border-border bg-background p-3 cursor-not-allowed select-none animate-slide-up-fade ${stagger}`}
    >
      {/* Top: icon pill + soon pill */}
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div
          className="relative h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: 'hsl(var(--palette-mint, 154 38% 94%) / 0.7)' }}
        >
          <Icon
            className="h-[15px] w-[15px] opacity-65"
            style={{ color: FOREST }}
            aria-hidden
          />
          {/* Tiny lock badge — anchors "not live yet" */}
          <span
            className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-background border border-border flex items-center justify-center"
            aria-hidden
          >
            <Lock className="h-2 w-2 text-muted-foreground" />
          </span>
        </div>
        <span
          className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded shrink-0 leading-none"
          style={{ backgroundColor: `${AMBER}18`, color: AMBER }}
        >
          Soon
        </span>
      </div>

      {/* Title + tagline */}
      <p className="text-xs font-semibold text-foreground/85 leading-tight line-clamp-2">
        {tool.title}
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug truncate">
        {tool.tagline}
      </p>
    </div>
  );
}
